<template>
  <div class="slide slide-stat-grid">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Stats Grid -->
    <div class="stats-grid" :class="`grid-${gridColumns}`">
      <div
        v-for="(metric, index) in data.metrics"
        :key="index"
        class="stat-card"
        :class="`border-${metric.color}`"
        :style="{ animationDelay: `${index * 0.08}s` }"
      >
        <div class="stat-stripe" :class="`bg-${metric.color}`"></div>
        <div class="stat-content">
          <div class="stat-value">
            {{ formatValue(metric) }}
          </div>
          <div class="stat-label">{{ metric.label }}</div>
          <div v-if="metric.description" class="stat-description">{{ metric.description }}</div>
          <div class="stat-change" :class="changeClass(metric)" v-if="metric.change !== undefined">
            <svg v-if="metric.trend === 'up'" class="trend-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5L5 12H9V19H15V12H19L12 5Z" fill="currentColor"/>
            </svg>
            <svg v-else-if="metric.trend === 'down'" class="trend-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 19L19 12H15V5H9V12H5L12 19Z" fill="currentColor"/>
            </svg>
            <span>{{ formatChange(metric.change) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StatGridSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    gridColumns() {
      const count = (this.data.metrics && this.data.metrics.length) || 0;
      if (count <= 3) return 3;
      if (count <= 4) return 4;
      return 5;
    }
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatValue(metric) {
      const value = metric.value;
      // NPS scores show + prefix for positive
      if (metric.isNPS || metric.label === 'eNPS' || metric.label === 'NPS') {
        return value > 0 ? `+${this.formatNumber(value)}` : this.formatNumber(value);
      }
      // Index scores (WHO-5, DEBI) don't have % suffix
      if (metric.isIndex) {
        return this.formatNumber(value);
      }
      // Default to percentage display or with suffix
      const formattedValue = this.formatNumber(value);
      return `${formattedValue}${metric.suffix !== undefined ? metric.suffix : '%'}`;
    },
    formatChange(change) {
      const prefix = change > 0 ? '+' : '';
      return `${prefix}${this.formatNumber(change)}`;
    },
    changeClass(metric) {
      if (metric.trend === 'up') return 'change-positive';
      if (metric.trend === 'down') return 'change-negative';
      return 'change-neutral';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-stat-grid {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 60px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.stats-grid {
  display: grid;
  gap: 24px;
  flex: 1;
  align-content: center;

  &.grid-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  &.grid-4 {
    grid-template-columns: repeat(4, 1fr);
  }

  &.grid-5 {
    grid-template-columns: repeat(5, 1fr);
  }
}

.stat-card {
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: $shadow-soft;
  transition: all 0.3s ease;
  animation: scaleIn 0.4s cubic-bezier(0.25, 1, 0.5, 1) backwards;

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-medium;
  }

  &.border-emerald .stat-stripe { background: $emerald; }
  &.border-amber .stat-stripe { background: $amber; }
  &.border-rose .stat-stripe { background: $rose; }
  &.border-violet .stat-stripe { background: $violet-500; }
}

.stat-stripe {
  height: 4px;
  width: 100%;
}

.stat-content {
  padding: 40px 32px;
  text-align: center;
}

.stat-value {
  font-family: $font-body;
  font-size: clamp(48px, 6vw, 72px);
  font-weight: 700;
  color: $ink;
  margin-bottom: 12px;
  line-height: 1;
}

.stat-label {
  font-family: $font-body;
  font-size: 18px;
  color: $ink-muted;
  margin-bottom: 8px;
}

.stat-description {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-subtle;
  margin-bottom: 12px;
  font-style: italic;
}

.stat-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: $font-mono;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 20px;

  &.change-positive {
    background: rgba($emerald, 0.1);
    color: $emerald;
  }

  &.change-negative {
    background: rgba($rose, 0.1);
    color: $rose;
  }

  &.change-neutral {
    background: rgba($ink, 0.05);
    color: $ink-muted;
  }

  .trend-icon {
    width: 16px;
    height: 16px;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
